import styled from 'styled-components';
import { colors, motion, radii, spacing, typography } from '../../../../theme-v2';
import type { QuickRecallMark } from './types';

/** Per-rating accent — "I knew this" is success green, "New to me" is error
 *  red. No shared `Button` variant carries these semantics. */
const ratingColors: Record<QuickRecallMark, { color: string; bg: string }> = {
  known: { color: colors.status.success, bg: colors.status.successBg },
  review: { color: colors.status.error, bg: colors.status.errorBg },
};

/** Perspective container that gives the flip its 3D depth. */
export const StyledCard = styled.div`
  position: relative;
  flex: 1;
  min-height: 240px;
  width: 100%;
  perspective: 1200px;
`;

export const StyledFlipInner = styled.div<{ $flipped: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform ${motion.duration.slow} ${motion.easing.standard};
  transform: ${({ $flipped }) => ($flipped ? 'rotateY(180deg)' : 'none')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * One card face. Both faces stack via `position:absolute`; the back is
 * pre-rotated so the flip reveals it, and `backface-visibility:hidden` keeps
 * the reversed side from showing through. Front is a dark "question" card,
 * back a light "answer" card.
 */
export const StyledFlipFace = styled.div<{ $back?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.base}px;
  padding: ${spacing.xl}px;
  border-radius: ${radii.box}px;
  backface-visibility: hidden;
  text-align: center;
  transform: ${({ $back }) => ($back ? 'rotateY(180deg)' : 'none')};
  ${({ $back }) =>
    $back
      ? `background: #EEF1FB; border: 1px solid ${colors.border.subtle};`
      : `background: linear-gradient(135deg, #1E2A5E 0%, #2E3C86 100%);`}
`;

/** Small uppercase eyebrow above the question / answer (e.g. "QUESTION 1 OF 5"). */
export const StyledEyebrow = styled.span<{ $onDark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs}px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.letterSpacing.wide};
  text-transform: uppercase;
  color: ${({ $onDark }) =>
    $onDark ? 'rgba(255, 255, 255, 0.7)' : colors.accent.active};
`;

/** "Flip to reveal" pill on the dark question face. */
export const StyledFlipButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs}px;
  padding: ${spacing.sm}px ${spacing.lg}px;
  border-radius: ${radii.full}px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: background ${motion.duration.fast} ${motion.easing.standard};

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

export const StyledRatingButton = styled.button<{
  $variant: QuickRecallMark;
  $active: boolean;
}>`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs}px;
  padding: ${spacing.sm}px ${spacing.md}px;
  border-radius: ${radii.md}px;
  border: 1px solid
    ${({ $variant, $active }) =>
      $active ? ratingColors[$variant].color : 'transparent'};
  background: ${({ $variant }) => ratingColors[$variant].bg};
  color: ${({ $variant }) => ratingColors[$variant].color};
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: filter ${motion.duration.fast} ${motion.easing.standard};

  &:hover {
    filter: brightness(0.97);
  }

  svg {
    flex: none;
  }
`;

/** One pagination dot; the active card's dot is the accent colour. */
export const StyledDot = styled.span<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: ${radii.full}px;
  background: ${({ $active }) =>
    $active ? colors.accent.base : colors.border.subtle};
  transition: background ${motion.duration.fast} ${motion.easing.standard};
`;
